import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { uploadImage } from '../services/dbService';
import toast from 'react-hot-toast';
import './ImageUpload.css';

// autoUpload: if true, upload immediately after file selection
const cloudConfigured = !!(process.env.REACT_APP_CLOUDINARY_CLOUD_NAME && process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);

const ImageUpload = forwardRef(({ onUpload, initialUrl = null, path = 'images', autoUpload = true }, ref) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(initialUrl);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    if (autoUpload) {
      if (!cloudConfigured) {
        toast.error('Cloudinary unsigned upload not configured. Add REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET to .env.local');
        return;
      }
      setTimeout(() => {
        handleUpload();
      }, 0);
    }
  };

  const handleUpload = async () => {
    if (!file) return null;
    setUploading(true);
    try {
      const url = await uploadImage(file, path);
      if (!url) throw new Error('No URL returned from upload endpoint');
      setProgress(100);
      setPreview(url);
      if (onUpload) onUpload(url);
      return url;
    } catch (err) {
      console.error('Upload failed', err);
      toast.error('Upload failed: ' + (err.message || 'See console'));
      throw err;
    } finally {
      setUploading(false);
      setFile(null);
    }
  };

  useImperativeHandle(ref, () => ({
    uploadIfPending: async () => {
      if (file) {
        return await handleUpload();
      }
      return preview; // may be null or existing URL
    },
    hasFile: () => !!file,
    isUploading: () => uploading,
  }));

  return (
    <div className="image-upload">
      <div className="image-preview">
        {preview ? <img src={preview} alt="preview" /> : <div className="placeholder">No image</div>}
      </div>
      <div className="image-controls">
        {!cloudConfigured && (
          <div className="upload-warning" style={{ color: '#b33', marginBottom: 8 }}>
            Cloudinary unsigned upload not configured. Set <code>REACT_APP_CLOUDINARY_CLOUD_NAME</code> and <code>REACT_APP_CLOUDINARY_UPLOAD_PRESET</code> in <code>.env.local</code>.
          </div>
        )}
        <input type="file" accept="image/*" onChange={handleFileChange} />
        <button onClick={handleUpload} disabled={!file || uploading || !cloudConfigured} className="btn btn-secondary">
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
        {progress > 0 && progress < 100 && <div className="progress">{progress}%</div>}
      </div>
    </div>
  );
});

export default ImageUpload;